import { useQuery } from '@tanstack/react-query'
import { Lock, TrendingUp, Zap, Users, Upload, Bot, Plug } from 'lucide-react'
import { api } from '../lib/api'
import { PageHeader } from '../components/ui/PageHeader'

// ---------------------------------------------------------------------------
// Tipi
// ---------------------------------------------------------------------------

interface UsageData {
  plan: string
  trial_expires_at: string | null
  limits: {
    max_uploads_month: number
    max_ai_calls_month: number
    max_connectors: number
    max_seats: number
  }
  usage: {
    uploads_this_month: number
    connectors_active: number
    ai_calls_this_month: number
    members_count: number
  }
}

// ---------------------------------------------------------------------------
// Costanti piano
// ---------------------------------------------------------------------------

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  starter: 'Starter',
  growth: 'Growth',
  enterprise: 'Enterprise',
}

const PLAN_COLORS: Record<string, string> = {
  trial: 'var(--km-text-muted)',
  starter: 'var(--km-primary)',
  growth: 'var(--km-success)',
  enterprise: '#a78bfa',
}

// Feature matrix: quali piani hanno accesso a quale feature
const FEATURES = [
  { label: 'Dashboard BI (5 sezioni)',   plans: ['trial', 'starter', 'growth', 'enterprise'] },
  { label: 'Upload file CSV/Excel',       plans: ['trial', 'starter', 'growth', 'enterprise'] },
  { label: 'Sorgenti cloud (Dropbox, S3)', plans: ['starter', 'growth', 'enterprise'], minPlan: 'Starter' },
  { label: 'AI Narrative (analisi testo)', plans: ['starter', 'growth', 'enterprise'], minPlan: 'Starter' },
  { label: 'AI Chat contestuale',          plans: ['starter', 'growth', 'enterprise'], minPlan: 'Starter' },
  { label: 'Export Excel / PDF',           plans: ['starter', 'growth', 'enterprise'], minPlan: 'Starter' },
  { label: 'Connettori avanzati (HubSpot, FIC)', plans: ['growth', 'enterprise'], minPlan: 'Growth' },
  { label: 'Collaboratori con viste parziali',   plans: ['growth', 'enterprise'], minPlan: 'Growth' },
  { label: 'Dashboard sharing (read-only)',       plans: ['growth', 'enterprise'], minPlan: 'Growth' },
  { label: 'Business Review board-ready',         plans: ['enterprise'], minPlan: 'Enterprise' },
  { label: 'SLA + supporto dedicato',             plans: ['enterprise'], minPlan: 'Enterprise' },
]

// ---------------------------------------------------------------------------
// Componenti
// ---------------------------------------------------------------------------

function UsageBar({
  label,
  icon: Icon,
  current,
  max,
}: {
  label: string
  icon: React.ElementType
  current: number
  max: number // 0 = illimitato
}) {
  const isUnlimited = max === 0
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((current / max) * 100))

  const barColor =
    isUnlimited
      ? 'var(--km-success)'
      : pct >= 95
      ? 'var(--km-error)'
      : pct >= 80
      ? 'var(--km-warning)'
      : 'var(--km-success)'

  const textColor =
    isUnlimited
      ? 'var(--km-success)'
      : pct >= 95
      ? 'var(--km-error)'
      : pct >= 80
      ? 'var(--km-warning)'
      : 'var(--km-text-muted)'

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <Icon size={15} style={{ color: 'var(--km-text-muted)' }} />
          <span style={{ color: 'var(--km-text-secondary)' }}>{label}</span>
        </div>
        <span className="font-semibold tabular-nums" style={{ color: textColor }}>
          {isUnlimited ? (
            <span style={{ color: 'var(--km-success)' }}>∞ illimitato</span>
          ) : (
            <>
              {current} <span style={{ color: 'var(--km-text-muted)', fontWeight: 400 }}>/ {max}</span>
            </>
          )}
        </span>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ background: 'var(--km-border)' }}
      >
        {!isUnlimited && (
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: barColor }}
          />
        )}
        {isUnlimited && (
          <div className="h-full rounded-full" style={{ width: '100%', background: 'var(--km-success)', opacity: 0.3 }} />
        )}
      </div>
      {!isUnlimited && pct >= 80 && (
        <p className="text-xs" style={{ color: textColor }}>
          {pct >= 95
            ? 'Limite quasi raggiunto — considera di fare upgrade.'
            : 'Stai usando oltre l\'80% del limite mensile.'}
        </p>
      )}
    </div>
  )
}

function FeatureRow({ label, available, minPlan }: { label: string; available: boolean; minPlan?: string }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b last:border-0" style={{ borderColor: 'var(--km-border)' }}>
      <span className="text-sm" style={{ color: available ? 'var(--km-text-primary)' : 'var(--km-text-muted)' }}>
        {label}
      </span>
      {available ? (
        <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ background: 'rgba(34,197,94,0.1)', color: 'var(--km-success)' }}>
          ✓ Incluso
        </span>
      ) : (
        <span
          className="flex items-center gap-1 text-xs px-2 py-0.5 rounded"
          style={{ background: 'var(--km-surface-2)', color: 'var(--km-text-muted)' }}
          title={`Disponibile dal piano ${minPlan}`}
        >
          <Lock size={11} /> dal piano {minPlan}
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pagina principale
// ---------------------------------------------------------------------------

export function PlanPage() {
  const { data, isLoading, error } = useQuery<UsageData>({
    queryKey: ['tenant', 'usage'],
    queryFn: () => api.get('/api/v1/tenants/me/usage'),
    refetchInterval: 60_000,
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Il mio piano" subtitle="Piano attivo e utilizzo corrente" />
        <div className="text-sm" style={{ color: 'var(--km-text-muted)' }}>Caricamento...</div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Il mio piano" subtitle="Piano attivo e utilizzo corrente" />
        <div className="text-sm" style={{ color: 'var(--km-error)' }}>Impossibile caricare i dati del piano.</div>
      </div>
    )
  }

  const { plan, trial_expires_at, limits, usage } = data
  const planLabel = PLAN_LABELS[plan] ?? plan
  const planColor = PLAN_COLORS[plan] ?? 'var(--km-text-primary)'
  const isEnterprise = plan === 'enterprise'
  const isTrial = plan === 'trial'

  return (
    <div className="space-y-8 max-w-2xl">
      <PageHeader title="Il mio piano" subtitle="Piano attivo e utilizzo corrente" />

      {/* Card piano attivo */}
      <div
        className="rounded-xl border p-6 flex items-start justify-between gap-4"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--km-text-muted)' }}>
            Piano attivo
          </p>
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold" style={{ color: planColor }}>
              {planLabel}
            </span>
            {isTrial && trial_expires_at && (
              <span
                className="text-xs px-2 py-1 rounded font-medium"
                style={{ background: 'rgba(234,179,8,0.15)', color: 'var(--km-warning)' }}
              >
                Scade il {new Date(trial_expires_at).toLocaleDateString('it-IT')}
              </span>
            )}
          </div>
          {isTrial && (
            <p className="text-sm mt-1" style={{ color: 'var(--km-text-muted)' }}>
              Stai usando la versione di prova di Koremargin.
            </p>
          )}
        </div>
        {!isEnterprise && (
          <a
            href="/upgrade"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-opacity hover:opacity-80 whitespace-nowrap"
            style={{ background: 'var(--km-gradient-brand)', color: '#fff' }}
          >
            <TrendingUp size={14} />
            Upgrade piano
          </a>
        )}
      </div>

      {/* Progress bar utilizzo */}
      <div
        className="rounded-xl border p-6 space-y-5"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <h3 className="text-sm font-semibold" style={{ color: 'var(--km-text-primary)' }}>
          Utilizzo questo mese
        </h3>
        <UsageBar
          label="Upload file"
          icon={Upload}
          current={usage.uploads_this_month}
          max={limits.max_uploads_month}
        />
        <UsageBar
          label="Chiamate AI"
          icon={Bot}
          current={usage.ai_calls_this_month}
          max={limits.max_ai_calls_month}
        />
        <UsageBar
          label="Connettori attivi"
          icon={Plug}
          current={usage.connectors_active}
          max={limits.max_connectors}
        />
        <UsageBar
          label="Collaboratori"
          icon={Users}
          current={usage.members_count}
          max={limits.max_seats}
        />
        <p className="text-xs pt-1" style={{ color: 'var(--km-text-muted)' }}>
          I contatori si azzerano il 1° di ogni mese.
        </p>
      </div>

      {/* Feature matrix */}
      <div
        className="rounded-xl border p-6"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--km-text-primary)' }}>
            Funzionalità incluse
          </h3>
          {!isEnterprise && (
            <a
              href="/upgrade"
              className="flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-80"
              style={{ color: 'var(--km-primary)' }}
            >
              <Zap size={12} />
              Sblocca tutto
            </a>
          )}
        </div>
        {FEATURES.map((f) => (
          <FeatureRow
            key={f.label}
            label={f.label}
            available={f.plans.includes(plan)}
            minPlan={f.minPlan}
          />
        ))}
      </div>
    </div>
  )
}
